import { Component, inject, OnInit } from '@angular/core';
import { RefreshableAttributeStateService } from '@app/services/refreshable-attribute-state.service';
import { OpenArhivWindowActionUtil } from '@app/shared/util/open-arhiv-window-util';
import { DynamicFormService } from '@ntap/common';
import {
  ActionWindowParams,
  EMPTY_ACTION_RESULT,
  Item,
  ItemActionResult,
  ItemUtil,
  NtapGlobalConstants,
  Util,
  ViewResult,
  ViewResultListener,
  ViewState,
  ViewWindowComponent,
  WindowRegistry
} from '@ntap/core';
import { PrimeNGButtonNewItemViewActionComponent } from '@ntap/primeng';
import { filter, first, map, Observable, of, switchMap, tap } from 'rxjs';

export type AddRelatedItemEntityActionConfig = {
  relatedTypeName: string;
  dependentAttribute: string;
};

@Component({
  selector: 'app-add-related-item-entity',
  templateUrl: './add-related-item-entity.component.html',
  providers: [
    {
      provide: ViewResultListener,
      useExisting: ViewWindowComponent
    },
    {
      provide: WindowRegistry,
      useClass: WindowRegistry
    },
    {
      provide: DynamicFormService,
      useClass: DynamicFormService
    }
  ]
})
export class AddRelatedItemEntityComponent
  extends PrimeNGButtonNewItemViewActionComponent
  implements OnInit
{
  public config$: Observable<AddRelatedItemEntityActionConfig>;

  private formService = inject(DynamicFormService, { skipSelf: true });
  private attributeStateService = inject(RefreshableAttributeStateService);

  ngOnInit(): void {
    this.config$ = this.action$.pipe(
      first(),
      map((a) => {
        const relatedAttr = a.depends_on_attribute;
        if (!relatedAttr) {
          throw new Error(
            `Invalid config for action ${a.name}. Required attribute: depends_on_attribute`
          );
        }
        return JSON.parse(
          a.depends_on_attribute
        ) as AddRelatedItemEntityActionConfig;
      })
    );
  }

  protected override updateItem(itemActionResult: ItemActionResult) {
    console.log(
      this.toString() + 'Apply new item' + JSON.stringify(itemActionResult)
    );
    this.config$
      .pipe(
        first(),
        switchMap((config) => {
          const control = this.formService.getFormControl(
            config.dependentAttribute
          );
          if (control) {
            return this.attributeStateService
              .refreshAllowedValues(config.dependentAttribute)
              .pipe(
                tap(() => {
                  const newValue: any = (itemActionResult.item as any)[
                    Util.getFullAttrName(
                      config.relatedTypeName,
                      NtapGlobalConstants.ATTR_id
                    )
                  ];
                  control.setValue(newValue);
                })
              );
          }
          return of({});
        })
      )
      .subscribe();
  }

  protected override doExecuteWithItem(
    item: Item
  ): Observable<ItemActionResult> {
    const keyItem = ItemUtil.extractItemKey(item);

    return this.doOpenWindow(keyItem).pipe(
      filter((viewResult) => this.acceptResult(viewResult)),
      tap(() => this.log('result ACCEPTED')),
      map(
        (viewResult) =>
          new ItemActionResult(EMPTY_ACTION_RESULT, viewResult.viewData[0])
      )
    );
  }

  private doOpenWindow(viewState: ViewState): Observable<ViewResult> {
    return OpenArhivWindowActionUtil.open(
      this.getActionWindowParams(),
      viewState
    );
  }

  protected override getActionWindowParams(): ActionWindowParams {
    this.doSetDefaultActionViewName();
    const actionWindowParams = OpenArhivWindowActionUtil.createParams(
      this.action,
      this.windowRegistry,
      this.getWindowTarget()
    );

    return actionWindowParams;
  }

  private doSetDefaultActionViewName() {
    if (!this.action.$openViewName) {
      this.action.$openViewName = '$defaultItemView';
    }
  }
}
